import React, { useState, useMemo, useCallback, FC } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import { v2Validate, v2IssueToken, DivisionMembership } from 'src/store/modules/auth/v2Operations';
import { saveDivisions, saveCurrentDivisionId, getDivisions, getCurrentDivisionId } from 'src/utils/divisionStorage';
import { clearDismissedMismatch } from 'src/hooks/useDivisionMismatch';
import { isV2Enabled } from 'src/utils/authVersion';
import colors from 'src/utils/colors';

const SEARCH_ICON = (
  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" style={{ flexShrink: 0, opacity: 0.3 }}>
    <circle cx="11" cy="11" r="8" stroke="#7E8FA8" strokeWidth="2" />
    <path d="M21 21l-4.35-4.35" stroke="#7E8FA8" strokeWidth="2" />
  </svg>
);

interface OmniDivisionPickerProps {
  appName: string;
}

const OmniDivisionPicker: FC<OmniDivisionPickerProps> = ({ appName }) => {
  const isV2 = isV2Enabled();
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());
  const [searchQuery, setSearchQuery] = useState('');
  const [switching, setSwitching] = useState(false);
  const [loaded, setLoaded] = useState(false);

  React.useEffect(() => {
    if (!isV2) {
      setLoaded(true);
      return;
    }
    const fetchDivisions = async () => {
      try {
        const response = await v2Validate();
        if (response.authenticated && response.divisions?.length > 0) {
          saveDivisions(response.divisions);
          setDivisions(response.divisions);
          if (response.division?.id) {
            setActiveDivisionId(response.division.id);
          }
        }
      } catch (error) {
        console.error('[OmniDivisionPicker] Failed to fetch divisions:', error);
      } finally {
        setLoaded(true);
      }
    };
    fetchDivisions();
  }, [isV2]);

  const filteredDivisions = useMemo(() => {
    if (!divisions) return [];
    if (!searchQuery.trim()) return divisions;
    const q = searchQuery.toLowerCase();
    return divisions.filter(
      (d) => d.name?.toLowerCase().includes(q) || d.companyName?.toLowerCase().includes(q)
    );
  }, [divisions, searchQuery]);

  const handleDivisionSwitch = useCallback(async (divisionId: string) => {
    if (divisionId === activeDivisionId) return;
    try {
      setSwitching(true);
      const validateResponse = await v2Validate();
      if (!validateResponse.authenticated || !validateResponse.sessionToken) {
        window.location.href = '/sign_out';
        return;
      }
      if (validateResponse.divisions?.length > 0) {
        saveDivisions(validateResponse.divisions);
      }
      await v2IssueToken(validateResponse.sessionToken, divisionId, appName);
      saveCurrentDivisionId(divisionId);
      clearDismissedMismatch();
      window.location.reload();
    } catch (error) {
      console.error('[OmniDivisionPicker] Failed to switch division:', error);
      setSwitching(false);
    }
  }, [activeDivisionId, appName]);

  if (!isV2 || !loaded || !divisions || divisions.length <= 1) {
    return null;
  }

  if (switching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  const showSearch = divisions.length > 2;

  return (
    <div style={{ padding: '0 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#7E8FA8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 6px' }}>
        Division
      </div>
      {showSearch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', marginBottom: 4, borderRadius: 6, background: '#F6F8FA' }}>
          {SEARCH_ICON}
          <input
            type="text"
            placeholder="Search divisions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13,
              width: '100%',
              color: '#333',
              fontFamily: 'inherit'
            }}
          />
        </div>
      )}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {filteredDivisions.length === 0 ? (
          <div style={{ padding: '8px', fontSize: 13, color: '#999', textAlign: 'center' }}>
            No divisions found
          </div>
        ) : (
          filteredDivisions.map((division) => {
            const isActive = division.id === activeDivisionId;
            return (
              <div
                key={division.id}
                onClick={() => handleDivisionSwitch(division.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDivisionSwitch(division.id); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px',
                  borderRadius: 6,
                  cursor: isActive ? 'default' : 'pointer',
                  background: isActive ? '#EBF0FF' : 'transparent',
                  transition: 'background 0.15s',
                  ...(isActive ? {} : { ':hover': { background: '#F6F8FA' } })
                }}
                onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '#F6F8FA'; }}
                onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? colors.blue : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {division.companyName ? `${division.companyName} - ${division.name}` : division.name}
                  </div>
                </div>
                {isActive && <CheckIcon style={{ fontSize: 16, color: colors.blue, flexShrink: 0, marginLeft: 8 }} />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OmniDivisionPicker;
